import { EditorView } from 'prosemirror-view'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import {
  closeLinkBubble,
  editLink,
  removeEditor,
  setLinkInputValue,
  showLinkBubble,
} from 'slices/richTextEditor'
import {
  selectIsLinkInvalid,
  selectLinkBubble,
  selectLinkInputValue,
} from 'slices/richTextEditor/selectors'
import {
  LinkBubbleData,
  RichTextEditorState,
} from 'slices/richTextEditor/types'
import { trimSelectionWhitespace } from '../helpers'
import editorSchema from '../schema'
import { validateUrl } from '../urlValidation'

type RootState = { richTextEditor: RichTextEditorState }

const LINK_BUBBLE_HEIGHT = 56
const LINK_BUBBLE_WIDTH = 320

let nextEditorId = 0
export function generateEditorId(): string {
  nextEditorId += 1
  return `rte-${nextEditorId}`
}

export function useLinkBubble(editorId: string) {
  const dispatch = useDispatch()
  const linkBubble = useSelector((state: RootState) =>
    selectLinkBubble(state, editorId)
  )
  const linkInputValue = useSelector((state: RootState) =>
    selectLinkInputValue(state, editorId)
  )
  const isLinkInvalid = useSelector((state: RootState) =>
    selectIsLinkInvalid(state, editorId)
  )

  useEffect(() => {
    return () => {
      dispatch(removeEditor(editorId))
    }
  }, [dispatch, editorId])

  const close = useCallback(() => {
    dispatch(closeLinkBubble(editorId))
  }, [dispatch, editorId])

  const edit = useCallback(() => {
    dispatch(editLink(editorId))
  }, [dispatch, editorId])

  const setInputValue = useCallback(
    (value: string) => {
      dispatch(setLinkInputValue({ editorId, value }))
    },
    [dispatch, editorId]
  )

  const show = useCallback(
    (bubble: LinkBubbleData) => {
      dispatch(showLinkBubble({ editorId, bubble }))
    },
    [dispatch, editorId]
  )

  return {
    linkBubble,
    linkInputValue,
    isLinkInvalid,
    closeLinkBubble: close,
    editLink: edit,
    setLinkInputValue: setInputValue,
    showLinkBubble: show,
  }
}

export function useLinkBubbleActions(
  editorId: string,
  editorViewRef: React.MutableRefObject<EditorView | null>,
  editorContainerRef: React.RefObject<HTMLDivElement>,
  bubble: ReturnType<typeof useLinkBubble>
) {
  const store = useStore()
  const { showLinkBubble: show, closeLinkBubble: close } = bubble

  const computeBubblePosition = useCallback(
    (selectionRect: DOMRect) => {
      if (!editorContainerRef.current) return { top: 0, left: 0 }
      const editorRect = editorContainerRef.current.getBoundingClientRect()
      const bubbleWidth = Math.min(LINK_BUBBLE_WIDTH, editorRect.width)
      const rawLeft =
        selectionRect.left -
        editorRect.left +
        selectionRect.width / 2 -
        bubbleWidth / 2
      const clampedLeft = Math.max(
        0,
        Math.min(rawLeft, editorRect.width - bubbleWidth)
      )
      return {
        top: selectionRect.top - editorRect.top - LINK_BUBBLE_HEIGHT,
        left: clampedLeft,
      }
    },
    [editorContainerRef]
  )

  const showBubble = useCallback(
    (overrideMode?: 'display' | 'edit') => {
      const view = editorViewRef.current
      if (!view || !editorContainerRef.current) return

      const { from, to } = view.state.selection
      if (from === to) return

      let existingHref = ''
      let linkFrom = from
      let linkTo = to
      let foundLink = false
      view.state.doc.nodesBetween(from, to, (node, pos) => {
        if (foundLink) return false
        const linkMark = node.marks.find(
          m => m.type === editorSchema.marks.link
        )
        if (linkMark) {
          existingHref = linkMark.attrs.href
          linkFrom = pos
          linkTo = pos + node.nodeSize
          foundLink = true
          return false
        }
      })

      const domSelection = window.getSelection()
      if (!domSelection || domSelection.rangeCount === 0) return
      const range = domSelection.getRangeAt(0)
      const { top, left } = computeBubblePosition(range.getBoundingClientRect())

      const mode = overrideMode || (existingHref ? 'display' : 'edit')
      bubble.setLinkInputValue(existingHref)
      show({
        url: existingHref,
        top,
        left,
        mode,
        linkFrom,
        linkTo,
      })
    },
    [editorViewRef, editorContainerRef, computeBubblePosition, bubble, show]
  )

  const applyLink = useCallback(() => {
    const view = editorViewRef.current
    // Read current state from Redux store — no stale closures
    const state = store.getState()
    const bubbleState = selectLinkBubble(state, editorId)
    const inputValue = selectLinkInputValue(state, editorId)
    if (!view || !bubbleState) return
    const { linkFrom: from, linkTo: to } = bubbleState

    if (from === to) {
      close()
      return
    }

    const result = validateUrl(inputValue)
    if (!result.valid) return

    const trimmed = trimSelectionWhitespace(view.state.doc, from, to)
    const tr = view.state.tr
    if (result.normalizedUrl) {
      const linkMark = editorSchema.marks.link.create({
        href: result.normalizedUrl,
      })
      tr.addMark(trimmed.from, trimmed.to, linkMark)
    } else {
      tr.removeMark(trimmed.from, trimmed.to, editorSchema.marks.link)
    }
    view.dispatch(tr)
    close()
    view.focus()
  }, [editorViewRef, store, editorId, close])

  const removeLink = useCallback(() => {
    const view = editorViewRef.current
    const state = store.getState()
    const bubbleState = selectLinkBubble(state, editorId)
    if (!view || !bubbleState) return

    const { linkFrom, linkTo } = bubbleState
    const tr = view.state.tr
    tr.removeMark(linkFrom, linkTo, editorSchema.marks.link)
    view.dispatch(tr)
    close()
    view.focus()
  }, [editorViewRef, store, editorId, close])

  const showLinkBubbleAtCursor = useCallback(() => {
    const view = editorViewRef.current
    if (!view || !editorContainerRef.current) return

    const { from } = view.state.selection
    const $pos = view.state.doc.resolve(from)
    const linkMark = $pos.marks().find(m => m.type === editorSchema.marks.link)
    if (!linkMark) return

    let linkFrom = from
    let linkTo = from
    const parent = $pos.parent
    const parentOffset = $pos.parentOffset
    parent.nodesBetween(0, parentOffset, (node, pos) => {
      if (node.marks.some(m => m.eq(linkMark))) {
        linkFrom = $pos.start() + pos
      } else {
        linkFrom = from
      }
    })
    let forwardDone = false
    parent.nodesBetween(parentOffset, parent.content.size, (node, pos) => {
      if (forwardDone) return
      if (node.marks.some(m => m.eq(linkMark))) {
        linkTo = $pos.start() + pos + node.nodeSize
      } else {
        forwardDone = true
      }
    })

    const startCoords = view.coordsAtPos(linkFrom)
    const endCoords = view.coordsAtPos(linkTo)
    const selectionRect = {
      top: Math.min(startCoords.top, endCoords.top),
      left: startCoords.left,
      width: endCoords.right - startCoords.left,
      right: endCoords.right,
      bottom: Math.max(startCoords.bottom, endCoords.bottom),
      height: 0,
    } as DOMRect
    const { top, left } = computeBubblePosition(selectionRect)

    bubble.setLinkInputValue(linkMark.attrs.href)
    show({
      url: linkMark.attrs.href,
      top,
      left,
      mode: 'display',
      linkFrom,
      linkTo,
    })
  }, [editorViewRef, editorContainerRef, computeBubblePosition, bubble, show])

  const handleEditorMouseUp = useCallback(() => {
    const view = editorViewRef.current
    if (!view) return
    setTimeout(() => {
      if (!editorViewRef.current) return
      const { from, to } = editorViewRef.current.state.selection
      if (from !== to) {
        showBubble()
      } else {
        const $pos = editorViewRef.current.state.doc.resolve(from)
        const hasLink = $pos
          .marks()
          .some(m => m.type === editorSchema.marks.link)
        if (hasLink) {
          showLinkBubbleAtCursor()
        } else {
          close()
        }
      }
    }, 0)
  }, [editorViewRef, showBubble, showLinkBubbleAtCursor, close])

  return { applyLink, removeLink, handleEditorMouseUp }
}
